export async function handleErrorResponse(
  res: Response,
  errorMsg = "Server error. Please contact support."
) {
  let notification: { text: string; type: "negative" };
  try {
    const dataRes = await res.json();
    notification = {
      text: dataRes.message || errorMsg,
      type: "negative",
    };
  } catch (e) {
    notification = {
      text: errorMsg,
      type: "negative",
    };
    return notification;
  }
  return notification;
}

/**
 * @param {T extends object} obj - generic object
 * @param {string[]} keysToOmit - array of keys to omit from the object.
 * @returns {Record<T extends object, K extends keyof T>} - object with the specified keys omitted.
 */
export const omitKeys = <T extends object, K extends keyof T>(
  obj: T,
  keysToOmit: K[]
): Omit<T, string> => {
  const result = structuredClone(obj); // deep copy
  keysToOmit.forEach((key) => {
    if (key in result) {
      delete result[key];
    }
  });
  return result;
};

/**
 * Utility to convert an object of key value pairs to URLSearchParams,
 * excluding undefined/null/empty values, which are not excluded by default
 * by the URLSearchParams API.
 *
 * @param params - object of key value pairs to convert to URLSearchParams
 * @returns URLSearchParams
 *
 * @example
 * const params = { tab: 'open', search: 'test', limit: 20, offset: 0 };
 * const searchParams = serializeQueryParams(params);
 * // searchParams.toString() === 'tab=open&limit=20&offset=0&search=test'
 *
 * Note: For improved UX, 'search' param is always last if it exists.
 */
export function serializeQueryParams<T extends Record<string, unknown>>(
  params: T
): URLSearchParams {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (key === "search") continue;
    if (value === undefined || value === "" || value === null) continue;

    if (Array.isArray(value)) {
      if (value.length > 0) {
        searchParams.set(key, value.join(","));
      }
    } else {
      searchParams.set(key, String(value));
    }
  }

  // Special handling for search. We want it to be the last parameter,
  // since it's distracting to have it change in the middle of the URL if you're typing
  if ("search" in params) {
    const search = params.search;
    if (search) {
      searchParams.set("search", String(search));
    }
  }
  return searchParams;
}

// Helper functions for LimitOffsetPagination

/**
 * @param {number} limit - limit of items per page.
 * @param {number} totalCount - total number of records stored in the DB.
 * the totalCount is the data.count returned from django LimitOffsetPagination.
 * https://www.django-rest-framework.org/api-guide/pagination/#limitoffsetpagination
 * @returns {number} the total number of pages a set of data will have.
 */
export const getTotalPages = (limit: number, totalCount: number) => {
  return Math.ceil(totalCount / limit);
};

/**
 * @param {number} offset - the current pagination offset int.
 * @param {number} limit - limit of items per page.
 * @returns {number} the current page number given the offset and limit.
 */
export const getSelectedPage = (offset: number, limit: number) => {
  return Math.ceil(offset / limit) + 1;
};

/**
 * @param {number} page - the current page the user is on.
 * @param {number} limit - limit of items per page.
 * @returns {number} api pagination offset based on the page and limit ints.
 */
export const getOffset = (page: number, limit: number) => {
  return (page - 1) * limit;
};

/**
 * Utility function to check if any filters are applied.
 * For list filters, we check if any of the arrays have a length greater than 0
 * For other filters, we check if the value is truthy
 *
 * @param filters - object containing filter values
 * @returns boolean - true if any filters are applied, false otherwise
 */
export const doesFilterExist = (filters: Record<string, unknown>) => {
  return Object.values(filters).some((value) => (Array.isArray(value) ? value.length > 0 : value));
};

/**
 * Retries a fetch call with exponential backoff on 5xx errors (e.g. S3 503 Slow Down).
 * Non-server errors and 4xx responses fail immediately without retrying.
 */
export async function fetchWithRetry(
  url: string,
  init: RequestInit,
  {
    maxRetries = 3,
    errorMessage = "Request failed after retries",
  }: { maxRetries?: number; errorMessage?: string } = {}
): Promise<Response> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const response = await fetch(url, init);
    if (response.ok || response.status < 500 || attempt === maxRetries) {
      return response;
    }
    const delay = 1000 * 2 ** attempt + Math.random() * 500;
    await new Promise((r) => setTimeout(r, delay));
  }
  // Unreachable, but satisfies TypeScript
  throw new Error(errorMessage);
}
